import type { PartnerCityId } from '../../content';
import {
  filterMockSuggestions,
  MOCK_CID_TO_PLACE_ID,
  MOCK_PLACE_BY_ID,
  MOCK_PLACES,
} from './mockPlacesData';
import type { PlaceAutocompleteParams, PlaceDetails, PlaceSuggestion } from './types';

const MOCK_LATENCY_MS = 450;

function delay<T>(value: T, ms = MOCK_LATENCY_MS): Promise<T> {
  return new Promise((resolve) => {
    window.setTimeout(() => resolve(value), ms);
  });
}

function extractPlaceIdFromMapsUrl(url: string): string | null {
  try {
    const parsed = new URL(url.trim());

    const cid = parsed.searchParams.get('cid');
    if (cid && MOCK_CID_TO_PLACE_ID[cid]) {
      return MOCK_CID_TO_PLACE_ID[cid];
    }

    const placeIdFromQuery = parsed.searchParams.get('query_place_id');
    if (placeIdFromQuery && MOCK_PLACE_BY_ID.has(placeIdFromQuery)) {
      return placeIdFromQuery;
    }

    const dataMatch = url.match(/!1s([A-Za-z0-9_-]+)/);
    if (dataMatch?.[1] && MOCK_PLACE_BY_ID.has(dataMatch[1])) {
      return dataMatch[1];
    }

    const pathMatch = url.match(/\/maps\/place\/[^/]+\/@[^/]+\/data=.*!1s([A-Za-z0-9_-]+)/);
    if (pathMatch?.[1] && MOCK_PLACE_BY_ID.has(pathMatch[1])) {
      return pathMatch[1];
    }

    const decoded = decodeURIComponent(url).toLowerCase();
    const byName = MOCK_PLACES.find((place) => {
      const slug = place.name.toLowerCase().replace(/\s+/g, '+');
      const spaced = place.name.toLowerCase();
      return decoded.includes(slug) || decoded.includes(spaced);
    });
    if (byName) return byName.placeId;

    return null;
  } catch {
    return null;
  }
}

/**
 * Places client — swap `USE_MOCK` for real fetch calls when backend is ready.
 *
 * Real endpoints (see package.json proxy → publicWebApi):
 *   GET /v1/places/autocomplete?q=&city=
 *   GET /v1/places/details?placeId=
 *   GET /v1/places/resolve-url?url=
 */
const USE_MOCK = true;

async function mockAutocomplete(params: PlaceAutocompleteParams): Promise<PlaceSuggestion[]> {
  return delay(filterMockSuggestions(params.query, params.cityId));
}

async function mockGetPlaceDetails(placeId: string): Promise<PlaceDetails | null> {
  return delay(MOCK_PLACE_BY_ID.get(placeId) ?? null);
}

async function mockResolveMapsUrl(url: string): Promise<PlaceDetails | null> {
  const placeId = extractPlaceIdFromMapsUrl(url);
  if (!placeId) return delay(null);
  return delay(MOCK_PLACE_BY_ID.get(placeId) ?? null);
}

async function realAutocomplete(params: PlaceAutocompleteParams): Promise<PlaceSuggestion[]> {
  const search = new URLSearchParams({ q: params.query });
  if (params.cityId) search.set('city', params.cityId);

  const response = await fetch(`/v1/places/autocomplete?${search.toString()}`, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error('autocomplete_failed');
  return (await response.json()) as PlaceSuggestion[];
}

async function realGetPlaceDetails(placeId: string): Promise<PlaceDetails | null> {
  const response = await fetch(
    `/v1/places/details?${new URLSearchParams({ placeId }).toString()}`,
    { headers: { Accept: 'application/json' } },
  );
  if (response.status === 404) return null;
  if (!response.ok) throw new Error('details_failed');
  return (await response.json()) as PlaceDetails;
}

async function realResolveMapsUrl(url: string): Promise<PlaceDetails | null> {
  const response = await fetch(
    `/v1/places/resolve-url?${new URLSearchParams({ url }).toString()}`,
    { headers: { Accept: 'application/json' } },
  );
  if (response.status === 404) return null;
  if (!response.ok) throw new Error('resolve_failed');
  return (await response.json()) as PlaceDetails;
}

export const placesClient = {
  autocomplete(params: PlaceAutocompleteParams): Promise<PlaceSuggestion[]> {
    return USE_MOCK ? mockAutocomplete(params) : realAutocomplete(params);
  },

  getPlaceDetails(placeId: string): Promise<PlaceDetails | null> {
    return USE_MOCK ? mockGetPlaceDetails(placeId) : realGetPlaceDetails(placeId);
  },

  resolveMapsUrl(url: string): Promise<PlaceDetails | null> {
    return USE_MOCK ? mockResolveMapsUrl(url) : realResolveMapsUrl(url);
  },
};

export function isValidMapsUrlInput(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  return (
    trimmed.includes('google.com/maps') ||
    trimmed.includes('maps.google.com') ||
    trimmed.includes('goo.gl/maps') ||
    trimmed.includes('maps.app.goo.gl')
  );
}

export type { PartnerCityId };
